import * as React from 'react';
import { useMemo } from 'react';

import { formatShortDateSafe, usePermissions } from '@klareospace/pharos-lib-front-utils';
import type { TemplateConfigurationWithResolvedRefs } from '@klareospace/pharos-lib-types';
import { TEMPLATE_CONFIGURATION_STATUS } from '@klareospace/pharos-lib-types';
import type { DataTableColumn, RowAction } from '@klareospace/pharos-lib-web-utils';
import { createActionsColumn, RowActionsMenu, ShipperStatusBadge } from '@klareospace/pharos-lib-web-utils';
import { Edit, PackageX, Pause, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ShipperNameCell } from '../components/shipper-name-cell';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ShipperRow {
  id: string;
  /**
   * Whether the backend resolved the template at list time.
   * The backend falls back `resolvedTemplate.name` to the raw `templateId` when
   * it cannot locate the template, so a `name` that still equals `templateId`
   * means unresolved. A distinct `name` is the authoritative resolution signal.
   */
  isTemplateResolved: boolean;
  lastEdit: string;
  logoUrl: string | undefined;
  name: string;
  status: TemplateConfigurationWithResolvedRefs['status'];
  templateId: string;
}

interface UseShippersTableColumnsParams {
  onEditRow: (row: ShipperRow) => void;
  onPauseRow: (row: ShipperRow) => void;
  onResumeRow: (row: ShipperRow) => void;
  onUninstallRow: (row: ShipperRow) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function toShipperRow(config: TemplateConfigurationWithResolvedRefs): ShipperRow {
  // The backend falls back `resolvedTemplate.name` to the raw `templateId` when
  // the template cannot be located. A `name` distinct from `templateId` is
  // therefore the authoritative signal that the template was resolved.
  return {
    id: config.id,
    isTemplateResolved: config.resolvedTemplate.name !== config.templateId,
    lastEdit: formatShortDateSafe(config.updatedAt),
    logoUrl: config.resolvedTemplate.logo || undefined,
    name: config.resolvedTemplate.name,
    status: config.status,
    templateId: config.templateId,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useShippersTableColumns({
  onEditRow,
  onPauseRow,
  onResumeRow,
  onUninstallRow,
}: UseShippersTableColumnsParams): DataTableColumn<ShipperRow>[] {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const { hasPermission } = usePermissions();

  return useMemo(() => {
    function getRowActions(row: ShipperRow): RowAction[] {
      // Archived rows should never appear in the list (excluded by POST /search),
      // but we gate defensively to prevent Edit/Uninstall on any stale archived row.
      if (row.status === TEMPLATE_CONFIGURATION_STATUS.ARCHIVED) {
        return [];
      }

      const actions: RowAction[] = [
        {
          icon: <Edit className="h-4 w-4" />,
          label: tCommon('actions.edit'),
          onClick: (): void => onEditRow(row),
          requiredPermission: 'shippers:manage',
        },
      ];

      // Pause/Resume: only shown for active or inactive rows (not pending/archived)
      if (row.status === TEMPLATE_CONFIGURATION_STATUS.ACTIVE) {
        actions.push({
          icon: <Pause className="h-4 w-4" />,
          label: t('shippers.actions.pause'),
          onClick: (): void => onPauseRow(row),
          requiredPermission: 'shippers:manage',
        });
      } else if (row.status === TEMPLATE_CONFIGURATION_STATUS.INACTIVE) {
        actions.push({
          icon: <Play className="h-4 w-4" />,
          label: t('shippers.actions.resume'),
          onClick: (): void => onResumeRow(row),
          requiredPermission: 'shippers:manage',
        });
      }

      actions.push({
        className: 'text-destructive',
        hasSeparator: true,
        icon: <PackageX className="h-4 w-4" />,
        label: tCommon('actions.uninstall'),
        onClick: (): void => onUninstallRow(row),
        requiredPermission: 'shippers:manage',
      });

      return actions;
    }

    return [
      {
        cell: (row): React.ReactNode => <ShipperNameCell logoUrl={row.logoUrl} name={row.name} />,
        cellClassName: 'truncate',
        className: 'w-[40%] min-w-[200px]',
        header: tCommon('fields.name'),
        key: 'name',
      },
      {
        cell: (row): React.ReactNode => (
          <ShipperStatusBadge label={tCommon(`statuses.${row.status}`)} status={row.status} />
        ),
        className: 'min-w-[120px]',
        header: tCommon('fields.status'),
        key: 'status',
      },
      {
        cell: (row): React.ReactNode => <span className="text-sm text-muted-foreground">{row.lastEdit}</span>,
        className: 'min-w-[160px]',
        header: t('shippers.table.lastEdit'),
        key: 'lastEdit',
      },
      createActionsColumn<ShipperRow>((row) => (
        <RowActionsMenu
          actions={getRowActions(row)}
          ariaLabel={tCommon('table.actionsColumn')}
          hasPermission={hasPermission}
        />
      )),
    ];
  }, [hasPermission, onEditRow, onPauseRow, onResumeRow, onUninstallRow, t]);
}
